/**
 * CodeEditor — syntax-highlighted code editor for the Code node.
 *
 * Features:
 *   - Prism.js highlighting for Python / JavaScript / Bash
 *   - Expand / fullscreen toggle
 *   - "Insert Reference" picker (prev node outputs, env vars, credentials, system vars)
 *   - Language-aware snippet insertion
 *   - Ctrl+A scoped to editor
 */
import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-bash';
import 'prismjs/themes/prism-dark.css';

const GRAMMAR_MAP = {
  python: { grammar: Prism.languages.python, name: 'python' },
  javascript: { grammar: Prism.languages.javascript, name: 'javascript' },
  bash: { grammar: Prism.languages.bash, name: 'bash' },
};

// ── Language-aware snippet builders ──────────────────────────────────

function nodeRef(lang, label, field) {
  const base = field ? `['${label}']['data']['${field}']` : `['${label}']`;
  if (lang === 'python') return `inputs${base}`;
  if (lang === 'javascript') return `inputs${base}`;
  if (lang === 'bash') {
    if (field) return `echo "$INPUTS" | jq -r '.["${label}"].data.${field}'`;
    return `echo "$INPUTS" | jq '.["${label}"]'`;
  }
  return `inputs${base}`;
}

function envRef(lang, key) {
  if (lang === 'python') return `os.environ['${key}']`;
  if (lang === 'javascript') return `process.env.${key}`;
  return `\$${key}`;
}

function nowRef(lang) {
  if (lang === 'python') return 'from datetime import datetime; now = datetime.utcnow().isoformat()';
  if (lang === 'javascript') return 'const now = new Date().toISOString();';
  return '$(date -u +%Y-%m-%dT%H:%M:%SZ)';
}

// ── Flatten nested output to dot-paths ──────────────────────────────

function flattenOutput(obj, prefix) {
  if (!obj || typeof obj !== 'object') return [];
  const result = [];
  for (const [k, v] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0 && Object.keys(v).length < 30) {
      result.push(...flattenOutput(v, path));
    } else {
      const preview = v === null ? 'null' : typeof v === 'object' ? '(object)' : String(v).slice(0, 30);
      result.push({ path, preview });
    }
  }
  return result;
}

// ── Styles ──────────────────────────────────────────────────────────

const TOOLBAR_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  background: '#181825',
  borderRadius: '6px 6px 0 0',
};

const TOOLBAR_BTN = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.12)',
  color: '#cdd6f4',
  padding: '3px 8px',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '11px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

const EDITOR_STYLE = {
  fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', 'Monaco', monospace",
  fontSize: '13px',
  lineHeight: '1.6',
  minHeight: '200px',
  outline: 'none',
  caretColor: '#fff',
};

const PICKER_STYLE = {
  position: 'absolute',
  top: '32px',
  right: '4px',
  width: 'min(300px, calc(100% - 8px))',
  background: 'var(--bg-secondary, #1e1e2e)',
  border: '1px solid var(--accent-color, #6366f1)',
  borderRadius: '6px',
  zIndex: 1200,
  maxHeight: '380px',
  overflowY: 'auto',
  overflowX: 'hidden',
  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
};

const PICKER_ROW = {
  padding: '4px 8px',
  cursor: 'pointer',
  borderRadius: '3px',
  display: 'flex',
  gap: '6px',
  alignItems: 'center',
};

const SECTION_HEADER = {
  padding: '5px 8px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  borderRadius: '4px',
  background: 'var(--bg-tertiary, #313244)',
  userSelect: 'none',
  marginBottom: '2px',
};


// =====================================================================
// CodeFieldPicker — the "Insert Reference" dropdown
// =====================================================================

function CodeFieldPicker({ language, ancestorNodes, nodeStatus, envVars, onInsert, onClose }) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: p[k] === undefined ? false : !p[k] }));
  const sl = search.toLowerCase();
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const hoverOn = (e) => { e.currentTarget.style.background = 'rgba(99,102,241,0.15)'; };
  const hoverOff = (e) => { e.currentTarget.style.background = 'transparent'; };

  return (
    <div ref={ref} style={PICKER_STYLE}>
      {/* Header + search */}
      <div style={{ padding: '8px', borderBottom: '1px solid var(--border-color, #444)', position: 'sticky', top: 0, background: 'var(--bg-secondary, #1e1e2e)', zIndex: 1 }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary, #888)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
          Insert Reference
        </div>
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search nodes, vars, helpers..."
          style={{ width: '100%', padding: '5px 8px', background: 'var(--bg-input, #11111b)', border: '1px solid var(--border-color, #444)', borderRadius: '4px', color: 'var(--text-primary, #cdd6f4)', fontSize: '12px', boxSizing: 'border-box' }}
        />
      </div>

      <div style={{ padding: '6px' }}>

        {/* ── Upstream Nodes ───────────────────────────────────── */}
        {ancestorNodes.map((node) => {
          const label = node.data?.label || node.id;
          const ns = nodeStatus?.[node.id];
          const fields = ns?.output ? flattenOutput(ns.output) : [];
          const filtered = sl ? fields.filter((f) => f.path.toLowerCase().includes(sl) || label.toLowerCase().includes(sl)) : fields;
          if (sl && filtered.length === 0 && !label.toLowerCase().includes(sl)) return null;

          const sKey = `node-${node.id}`;
          const isOpen = expanded[sKey] !== false;

          return (
            <div key={node.id} style={{ marginBottom: '3px' }}>
              <div onClick={() => toggle(sKey)} style={SECTION_HEADER}>
                <span style={{ fontSize: '10px', color: 'var(--text-tertiary, #888)' }}>{isOpen ? '▼' : '▶'}</span>
                <span style={{ fontSize: '12px', fontWeight: 600, flex: 1, color: 'var(--text-primary, #cdd6f4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                <button
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onInsert(nodeRef(language, label)); }}
                  title="Insert full output reference"
                  style={{ fontSize: '10px', padding: '1px 5px', background: 'var(--bg-input, #11111b)', border: '1px solid var(--border-color, #444)', borderRadius: '3px', cursor: 'pointer', color: 'var(--text-secondary, #aaa)', flexShrink: 0 }}
                >full</button>
              </div>
              {isOpen && (
                <div style={{ paddingLeft: '10px' }}>
                  {filtered.length === 0 ? (
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary, #888)', padding: '4px 6px', fontStyle: 'italic' }}>
                      {ns ? 'No output fields.' : 'Run workflow to see fields.'}
                    </div>
                  ) : filtered.slice(0, 20).map(({ path, preview }) => (
                    <div
                      key={path}
                      onMouseDown={(e) => { e.preventDefault(); onInsert(nodeRef(language, label, path)); }}
                      style={PICKER_ROW}
                      onMouseEnter={hoverOn}
                      onMouseLeave={hoverOff}
                    >
                      <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--accent-color, #6366f1)', flex: 1 }}>.{path}</span>
                      <span style={{ fontSize: '10px', color: 'var(--text-tertiary, #888)', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* ── Environment Variables ────────────────────────────── */}
        {envVars && envVars.length > 0 && (() => {
          const filteredEnv = sl ? envVars.filter((v) => v.key.toLowerCase().includes(sl)) : envVars;
          if (sl && filteredEnv.length === 0) return null;
          const isOpen = expanded['env'] !== false;
          return (
            <div style={{ marginTop: '4px', borderTop: '1px solid var(--border-color, #444)', paddingTop: '6px' }}>
              <div onClick={() => toggle('env')} style={SECTION_HEADER}>
                <span style={{ fontSize: '10px', color: 'var(--text-tertiary, #888)' }}>{isOpen ? '▼' : '▶'}</span>
                <span style={{ fontSize: '12px', fontWeight: 600, flex: 1, color: 'var(--text-primary, #cdd6f4)' }}>Environment Variables</span>
                <span style={{ fontSize: '10px', color: 'var(--text-tertiary, #888)' }}>{filteredEnv.length}</span>
              </div>
              {isOpen && filteredEnv.map((v) => (
                <div
                  key={v.key}
                  onMouseDown={(e) => { e.preventDefault(); onInsert(envRef(language, v.key)); }}
                  style={{ ...PICKER_ROW, paddingLeft: '16px' }}
                  onMouseEnter={hoverOn}
                  onMouseLeave={hoverOff}
                >
                  <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--accent-color, #6366f1)', flex: 1 }}>{v.key}</span>
                  <span style={{ fontSize: '10px', color: 'var(--text-tertiary, #888)' }}>{v.is_encrypted ? '(encrypted)' : String(v.value || '').slice(0, 20)}</span>
                </div>
              ))}
            </div>
          );
        })()}

        {/* ── Credential Env Vars ─────────────────────────────── */}
        {(!sl || 'cred'.includes(sl) || 'credential'.includes(sl)) && (
          <div style={{ marginTop: '4px', borderTop: '1px solid var(--border-color, #444)', paddingTop: '6px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-tertiary, #888)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '2px 8px', marginBottom: '3px' }}>
              Credential Env Vars
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary, #888)', padding: '2px 8px', marginBottom: '4px' }}>
              Pick a credential above — its fields become <code style={{ color: 'var(--accent-color, #6366f1)' }}>CRED_*</code> env vars.
            </div>
            {[
              { snippet: envRef(language, 'CRED_API_KEY'), label: 'CRED_API_KEY', desc: 'API key' },
              { snippet: envRef(language, 'CRED_TOKEN'), label: 'CRED_TOKEN', desc: 'Token' },
              { snippet: envRef(language, 'CRED_USERNAME'), label: 'CRED_USERNAME', desc: 'Username' },
              { snippet: envRef(language, 'CRED_PASSWORD'), label: 'CRED_PASSWORD', desc: 'Password' },
            ].filter((s) => !sl || s.label.toLowerCase().includes(sl)).map((s) => (
              <div
                key={s.label}
                onMouseDown={(e) => { e.preventDefault(); onInsert(s.snippet); }}
                style={{ ...PICKER_ROW, paddingLeft: '8px' }}
                onMouseEnter={hoverOn}
                onMouseLeave={hoverOff}
              >
                <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--accent-color, #6366f1)', flex: 1 }}>{s.label}</span>
                <span style={{ fontSize: '10px', color: 'var(--text-tertiary, #888)' }}>{s.desc}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── System Variables & Snippets ─────────────────────── */}
        {(!sl || ['now', 'date', 'time', 'json', 'import', 'require', 'print', 'console'].some((k) => k.includes(sl))) && (
          <div style={{ marginTop: '4px', borderTop: '1px solid var(--border-color, #444)', paddingTop: '6px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-tertiary, #888)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '2px 8px', marginBottom: '3px' }}>
              Snippets
            </div>
            {[
              { snippet: nowRef(language), label: 'Current datetime', desc: language === 'bash' ? 'date' : 'now' },
              ...(language === 'python' ? [
                { snippet: 'import json\ndata = json.loads(raw)', label: 'Parse JSON', desc: 'json.loads' },
                { snippet: 'import requests\nresp = requests.get(url)\noutput = resp.json()', label: 'HTTP GET', desc: 'requests' },
                { snippet: 'output = {"result": value}', label: 'Set output', desc: 'return data' },
              ] : []),
              ...(language === 'javascript' ? [
                { snippet: 'const data = JSON.parse(raw);', label: 'Parse JSON', desc: 'JSON.parse' },
                { snippet: 'output = { result: value };', label: 'Set output', desc: 'return data' },
              ] : []),
              ...(language === 'bash' ? [
                { snippet: 'echo "$INPUTS" | jq \'.\'', label: 'Parse inputs (jq)', desc: 'jq' },
                { snippet: 'curl -s "$URL" | jq \'.\'', label: 'HTTP GET + parse', desc: 'curl + jq' },
                { snippet: 'echo \'{"result": "value"}\'', label: 'Output JSON', desc: 'stdout JSON' },
              ] : []),
            ].filter((s) => !sl || s.label.toLowerCase().includes(sl) || s.desc.toLowerCase().includes(sl)).map((s, i) => (
              <div
                key={i}
                onMouseDown={(e) => { e.preventDefault(); onInsert(s.snippet); }}
                style={PICKER_ROW}
                onMouseEnter={hoverOn}
                onMouseLeave={hoverOff}
              >
                <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--accent-color, #6366f1)', flex: 1 }}>{s.label}</span>
                <span style={{ fontSize: '10px', color: 'var(--text-tertiary, #888)' }}>{s.desc}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// =====================================================================
// CodeEditor (main export)
// =====================================================================

export default function CodeEditor({
  value = '',
  onChange,
  language = 'python',
  placeholder = '',
  ancestorNodes = [],
  nodeStatus = {},
  envVars = [],
}) {
  const [expanded, setExpanded] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const editorRef = useRef(null);

  const { grammar, name } = GRAMMAR_MAP[language] || GRAMMAR_MAP.python;

  const highlight = useCallback(
    (code) => Prism.highlight(code || '', grammar, name),
    [grammar, name],
  );

  // Insert snippet at cursor (or append)
  const insertSnippet = useCallback((snippet) => {
    // Find the textarea inside the editor
    const ta = editorRef.current?.querySelector('textarea');
    if (ta) {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const before = value.substring(0, start);
      const after = value.substring(end);
      const needsNewline = before.length > 0 && !before.endsWith('\n') && snippet.includes('\n');
      const insert = (needsNewline ? '\n' : '') + snippet;
      onChange(before + insert + after);
      // Restore cursor after insert
      setTimeout(() => {
        ta.focus();
        ta.selectionStart = ta.selectionEnd = start + insert.length;
      }, 0);
    } else {
      // Fallback: append
      onChange(value + (value && !value.endsWith('\n') ? '\n' : '') + snippet);
    }
    setShowPicker(false);
  }, [value, onChange]);

  // Ctrl+A scopes to editor; Escape exits fullscreen
  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
      e.stopPropagation();
    }
    if (e.key === 'Escape' && expanded) {
      e.preventDefault();
      setExpanded(false);
    }
  }, [expanded]);

  const containerStyle = useMemo(() => expanded ? {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2000,
    background: '#1e1e2e',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
  } : {
    position: 'relative',
    borderRadius: '6px',
    border: '1px solid var(--border-color)',
    background: '#1e1e2e',
    display: 'flex',
    flexDirection: 'column',
  }, [expanded]);

  const editorWrapperStyle = useMemo(() => ({
    flex: 1,
    overflow: 'auto',
    maxHeight: expanded ? 'none' : '400px',
    minHeight: expanded ? 0 : '200px',
  }), [expanded]);

  const padding = useMemo(() => ({ top: 12, right: 12, bottom: 12, left: 12 }), []);

  return (
    <div style={containerStyle} onKeyDown={handleKeyDown} ref={editorRef}>
      {/* ── Toolbar ──────────────────────────────────── */}
      <div style={TOOLBAR_STYLE}>
        <span style={{ fontSize: '10px', color: '#6c7086', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 }}>
          {language}
        </span>
        <button
          onClick={() => setShowPicker((p) => !p)}
          style={{ ...TOOLBAR_BTN, color: showPicker ? 'var(--accent-color, #6366f1)' : '#cdd6f4' }}
          title="Insert field reference, env var, or snippet"
        >
          {'{ }'}
          <span style={{ fontSize: '10px' }}>Insert</span>
        </button>
        <button
          onClick={() => setExpanded((p) => !p)}
          style={TOOLBAR_BTN}
          title={expanded ? 'Collapse editor (Esc)' : 'Expand editor to fullscreen'}
        >
          {expanded ? '⤓' : '⤢'}
        </button>
      </div>

      {/* ── Picker dropdown ──────────────────────────── */}
      {showPicker && (
        <CodeFieldPicker
          language={language}
          ancestorNodes={ancestorNodes}
          nodeStatus={nodeStatus}
          envVars={envVars}
          onInsert={insertSnippet}
          onClose={() => setShowPicker(false)}
        />
      )}

      {/* ── Editor ───────────────────────────────────── */}
      <div style={editorWrapperStyle}>
        <Editor
          value={value}
          onValueChange={onChange}
          highlight={highlight}
          padding={padding}
          placeholder={placeholder || `// Write your ${language} code here`}
          style={{ ...EDITOR_STYLE, minHeight: expanded ? '100%' : '200px' }}
          textareaClassName="code-editor-textarea"
          preClassName="code-editor-pre"
          tabSize={4}
          insertSpaces
        />
      </div>
    </div>
  );
}
